import * as React from 'react';
import useStyles from './use_styles';
import useControlled from '../utils/use_controlled';
import { RadioButtonInterface } from './types';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import { useTheme } from '@veneer/theme';

import { warnDisabledConflict } from '../utils/warnDisabledConflict';

let warned = false;

const RadioButton = React.forwardRef<
  HTMLInputElement,
  RadioButtonInterface &
    Omit<React.HTMLAttributes<HTMLInputElement>, keyof RadioButtonInterface>
>(
  (
    {
      'aria-disabled': ariaDisabled,
      checked: checkedProp,
      className,
      defaultChecked,
      disabled: disabledProp = false,
      id,
      label,
      name,
      onChange,
      value,
      ...inputProps
    },
    ref
  ) => {
    warned = warnDisabledConflict(disabledProp, ariaDisabled, warned);

    const [checked, setChecked] = useControlled({
      controlled: checkedProp,
      default: Boolean(defaultChecked),
    });

    const theme = useTheme();
    const isHc = theme.mode === 'contrast';
    const disabled =
      ariaDisabled === true || ariaDisabled === 'true' || disabledProp;

    const handleChange = React.useCallback(
      (event) => {
        if (disabled) return;

        setChecked(event.target.checked);

        onChange?.(event);
      },
      [disabled, onChange, setChecked]
    );

    const style = useStyles();

    return (
      <label
        className={className}
        css={[
          style.root.base,
          checked && style.root.checked,
          disabled && style.root.disabled,
          isHc && [style.hc.root.base, disabled && style.hc.root.disabled],
          highContrastMediaQuery(theme),
        ]}
      >
        <input
          css={style.interactive}
          checked={checkedProp}
          className="vn-radio-button__input"
          disabled={disabledProp}
          aria-disabled={ariaDisabled}
          id={id}
          name={name}
          onChange={handleChange}
          ref={ref}
          type="radio"
          value={value}
          {...inputProps}
        />

        <span
          aria-hidden
          css={[
            style.decorative.base,
            checked && style.decorative.checked,
            disabled && style.decorative.disabled,
          ]}
          className="vn-radio-button__icon"
        />

        {label && (
          <span css={style.label} className="label vn-radio-button__span-text">
            {label}
          </span>
        )}
      </label>
    );
  }
);

RadioButton.displayName = 'RadioButton';

export default RadioButton;
